import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ClipboardList,
  ExternalLink,
  Lock,
  Search,
} from "lucide-react";
import {
  FORM_STATUSES,
  FORM_STATUS_LABEL,
  type FormStatus,
} from "@colab/shared";
import { cn, formatRelative } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useForms } from "./use-forms";
import { FormStatusBadge } from "./form-status-badge";
import { CreateFormDialog } from "./create-form-dialog";
import type { AdminFormRow } from "./types";

// Phase 7 sub-bucket 11 — admin forms list. Ports
// /app/admin/forms/page.tsx with the sub-bucket 10 conventions:
//   - URL search params for ?q= and ?status= filter + ?sort= / ?dir= sort
//   - Custom <table> with SortableHeader buttons + hover group + row link
//   - Shared <EmptyState> for loading-error and empty cases
//   - <CreateFormDialog> Dialog modal (matches legacy CreateFormButton)
//
// Submission counts (Submissions / Pending / Accepted / Declined columns +
// the four MetricCards from legacy) are deferred — they require a
// /api/admin/forms/submissions-aggregate read that doesn't exist yet (Phase
// 6 ported the form mutations but not the submission aggregate; the legacy
// page ran the aggregate as an inline supabase query). Adding the aggregate
// endpoint here would mean a fresh sub-bucket; leaving the counts out keeps
// the strict-parity scope honest and saves the aggregate for the submissions
// admin sub-bucket which already owns that surface.

type SortDir = "asc" | "desc";
type SortKey = "name" | "status" | "updated_at" | "created_at";

const SORTABLE: ReadonlySet<SortKey> = new Set([
  "name",
  "status",
  "updated_at",
  "created_at",
]);

export function FormsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useForms();

  const q = searchParams.get("q") ?? "";
  const statusFilter = searchParams.get("status") as FormStatus | null;
  const sortParam = (searchParams.get("sort") ?? "updated_at") as SortKey;
  const sortKey: SortKey = SORTABLE.has(sortParam) ? sortParam : "updated_at";
  const dirParam = (searchParams.get("dir") ?? "desc") as SortDir;
  const dir: SortDir = dirParam === "asc" ? "asc" : "desc";

  const filtersActive = Boolean(q.trim()) || Boolean(statusFilter);

  const filtered = useMemo(() => {
    const rows = query.data ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((f) => {
      if (needle && !f.name.toLowerCase().includes(needle) && !f.slug.toLowerCase().includes(needle)) {
        return false;
      }
      if (statusFilter && f.status !== statusFilter) return false;
      return true;
    });
  }, [query.data, q, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const factor = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * factor;
      }
      const as = String(av ?? "");
      const bs = String(bv ?? "");
      return as.localeCompare(bs) * factor;
    });
    return arr;
  }, [filtered, sortKey, dir]);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  function toggleSort(key: SortKey) {
    if (!SORTABLE.has(key)) return;
    const next = new URLSearchParams(searchParams);
    if (sortKey !== key) {
      next.set("sort", key);
      next.set("dir", "asc");
    } else {
      next.set("dir", dir === "asc" ? "desc" : "asc");
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Forms
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Build and publish forms for partner registration, applications,
            and one-off captures.
          </p>
        </div>
        <CreateFormDialog />
      </header>

      <Toolbar q={q} statusFilter={statusFilter} onChange={setParam} />

      <div className="mt-6">
        {query.isLoading ? (
          <TableSkeleton />
        ) : query.isError ? (
          <EmptyState
            icon={ClipboardList}
            title="Couldn't load forms"
            description={query.error?.message ?? "Try refreshing."}
          />
        ) : sorted.length > 0 ? (
          <FormsTable rows={sorted} sortKey={sortKey} dir={dir} onSort={toggleSort} />
        ) : filtersActive ? (
          <EmptyState
            icon={Search}
            title="No forms match these filters"
            description="Try clearing the search box or relaxing the filters."
          />
        ) : (
          <EmptyState
            icon={ClipboardList}
            title="No forms yet"
            description="Create a form to start collecting structured submissions from partners or the public."
            action={<CreateFormDialog variant="empty" />}
          />
        )}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Looking for incoming submissions?{" "}
        <Link
          to="/admin/submissions"
          className="underline underline-offset-2 hover:text-foreground"
        >
          Open the submissions queue
        </Link>
        .
      </p>
    </div>
  );
}

function Toolbar({
  q,
  statusFilter,
  onChange,
}: {
  q: string;
  statusFilter: FormStatus | null;
  onChange: (key: string, value: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          placeholder="Search by name or slug"
          onChange={(e) => onChange("q", e.target.value)}
          className="pl-8"
        />
      </div>
      <FilterGroup<FormStatus>
        label="Status"
        value={statusFilter}
        options={FORM_STATUSES.map((v) => ({
          value: v,
          label: FORM_STATUS_LABEL[v],
        }))}
        onChange={(next) => onChange("status", next)}
      />
    </div>
  );
}

function FilterGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T | null) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-border/70 bg-card/40 px-1.5 py-1">
      <span className="px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(value === o.value ? null : o.value)}
          className={cn(
            "rounded px-2 py-0.5 text-[11px] font-medium transition-colors",
            value === o.value
              ? "bg-accent/15 text-accent"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function FormsTable({
  rows,
  sortKey,
  dir,
  onSort,
}: {
  rows: AdminFormRow[];
  sortKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <SortableHeader name="name" label="Name" sortKey={sortKey} dir={dir} onSort={onSort} />
            <SortableHeader name="status" label="Status" sortKey={sortKey} dir={dir} onSort={onSort} />
            <SortableHeader name="updated_at" label="Updated" sortKey={sortKey} dir={dir} onSort={onSort} align="right" />
            <SortableHeader name="created_at" label="Created" sortKey={sortKey} dir={dir} onSort={onSort} align="right" />
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((row) => (
            <tr key={row.id} className="group transition-colors hover:bg-muted/30">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/forms/${row.id}/edit`}
                    className="font-medium text-foreground hover:text-accent"
                  >
                    {row.name}
                  </Link>
                  {row.is_system && (
                    <span
                      className="inline-flex items-center text-muted-foreground/70"
                      title="System form — cannot be deleted"
                      aria-label="System form"
                    >
                      <Lock className="h-3 w-3" />
                    </span>
                  )}
                </div>
                <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  /{row.slug}
                </div>
              </td>
              <td className="px-4 py-3">
                <FormStatusBadge status={row.status} />
              </td>
              <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                {formatRelative(row.updated_at)}
              </td>
              <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                {formatRelative(row.created_at)}
              </td>
              <td className="px-4 py-3 text-right">
                <Link
                  to={`/admin/forms/${row.id}/edit`}
                  aria-label="Open form"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:bg-background hover:text-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SortableHeader({
  name,
  label,
  sortKey,
  dir,
  onSort,
  align = "left",
}: {
  name: SortKey;
  label: string;
  sortKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const isActive = sortKey === name;
  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-2.5 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(name)}
        className={cn(
          "inline-flex items-center gap-1 transition hover:text-foreground",
          isActive && "text-foreground",
        )}
      >
        {label}
        <Icon className="h-3 w-3 opacity-70" />
      </button>
    </th>
  );
}

function TableSkeleton() {
  return (
    <div className="rounded-xl border bg-card">
      <div className="space-y-3 p-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="ml-auto h-4 w-16" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
